import { Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useParams } from "react-router-dom";
import ParticipantListEditorActionBar from "./ParticipantListEditorActionBar";
import ParticipantListMainContent from "./ParticipantListMainContent";
import ParticipantListVersionStatusRow from "./ParticipantListVersionStatusRow";
import ParticipantListViewActionBar from "./ParticipantListViewActionBar";
import { useParticipantListsPageState } from "./useParticipantListsPageState";

export interface PublicOrderParticipantListsProps {
  readonly signedLinkToken: string;
}

const PublicOrderParticipantLists: FC<PublicOrderParticipantListsProps> = ({ signedLinkToken }) => {
  const { orderId } = useParams<{ orderId: string }>();

  const {
    template,
    isBootstrapping,
    participantLists,
    editMode,
    setSelectedVersionIdx,
    editor,
    lastSavedList,
    showEditor,
    resolvedVersionIdx,
    selectedList,
    previousList,
    noParticipantsRequired,
    handleEdit,
    handleCancelEdit,
    handleSave,
    handleUploadSpreadsheet,
  } = useParticipantListsPageState(orderId!, { signedLinkToken });

  if (isBootstrapping) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!template) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h6">Link jest niepełny lub wygasł</Typography>
        <Typography
          variant="body2"
          sx={{
            marginTop: 1,
          }}
        >
          Skontaktuj się z biurem podróży, aby otrzymać poprawny link do listy uczestników.
        </Typography>
      </Grid>
    );
  }

  const orderUserFriendlyId = template.order.userFriendlyId;
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid size={12}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "baseline",
          }}
        >
          <Typography variant="h6">Zamówienie {orderUserFriendlyId}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Uzupełnij dane i zapisz listę.
          </Typography>
        </Stack>
      </Grid>
      {!showEditor && participantLists.length > 0 ? (
        <ParticipantListVersionStatusRow
          resolvedVersionIdx={resolvedVersionIdx}
          participantLists={participantLists}
          selectedList={selectedList}
          onVersionChange={setSelectedVersionIdx}
        />
      ) : null}
      <Grid size={12}>
        <ParticipantListMainContent
          noParticipantsRequired={noParticipantsRequired}
          showEditor={showEditor}
          template={template}
          lastSavedList={lastSavedList}
          editor={editor}
          selectedList={selectedList}
          previousList={previousList}
          showDiffs={resolvedVersionIdx > 0}
        />
      </Grid>
      {!noParticipantsRequired ? (
        <Grid
          size={12}
          container
          sx={{
            justifyContent: "flex-end",
          }}
        >
          {showEditor ? (
            <ParticipantListEditorActionBar
              orderId={orderId!}
              orderUserFriendlyId={orderUserFriendlyId}
              signedLinkToken={signedLinkToken}
              editMode={editMode}
              onCancelEdit={handleCancelEdit}
              onReset={editor.handleReset}
              onSave={handleSave}
              onUploadSpreadsheet={handleUploadSpreadsheet}
              submitEnabled={editor.submitEnabled}
            />
          ) : (
            <ParticipantListViewActionBar onEdit={handleEdit} />
          )}
        </Grid>
      ) : null}
    </Grid>
  );
};

export default PublicOrderParticipantLists;
